import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SkeletonText } from '../../components';
import { praiseAPI, organizationAPI, mediaAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isAxiosError } from '../../utils/errorUtils';
import { queryClient } from '../../lib/queryClient';
import { triggerHaptic } from '../../utils/haptics';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { TabBar } from '../ui/TabBar';
import { Modal } from '../ui/Modal';
import { isDomRole } from '../../utils/roleHelpers';
import { useActiveRole } from '../../hooks/useActiveRole';
import { queryKeys } from '../../hooks/queries/queryKeys';

interface PraiseTemplate {
  id: number;
  title: string;
  message: string;
  emoji: string;
  is_system_default: boolean;
}

interface OrganizationMember {
  id: string;
  userId: string;
  userName: string;
  displayName?: string;
  email: string;
  role: string;
}

interface QuickPraiseModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  onPraiseSent?: () => void;
  preselectedRecipient?: { id: string; name: string };
}

const QuickPraiseModal: React.FC<QuickPraiseModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  onPraiseSent,
  preselectedRecipient,
}) => {
  const { user } = useAuth();
  // Spec 274 item 2: "Points to Award" is Dom-only authority. For a switch
  // member, that authority follows active_role, not the raw org role —
  // isDomRole(currentUserMember?.role) alone would keep showing the panel
  // to a switch who has toggled to submissive for the week.
  const { dynamicRole: myDynamicRole, activeRole: myActiveRole } = useActiveRole(organizationId, user?.id);
  const [selectedTemplate, setSelectedTemplate] = useState<PraiseTemplate | null>(null);
  const [selectedRecipient, setSelectedRecipient] = useState<string>('');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [view, setView] = useState<'templates' | 'custom'>('templates');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [customPoints, setCustomPoints] = useState<number | ''>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Bundles GET /praise/templates + GET /organizations/:orgId/members --
  // mirrors pre-migration loadData()'s Promise.all fetch-and-render unit.
  // staleTime: 0 + refetchOnWindowFocus: false (rather than the 30s-fresh/
  // refetch-on-focus global defaults), matching QuickCreateModal.tsx's own
  // "fetched fresh every time the modal opens" precedent: pre-migration was
  // a one-shot fetch per open with no re-fetching at all, so a stale-cache
  // hit on reopen or a background focus refetch would both be new behavior.
  const contextQuery = useQuery({
    queryKey: queryKeys.quickPraise.context(organizationId || ''),
    queryFn: async () => {
      const [templatesRes, membersRes] = await Promise.all([
        praiseAPI.getTemplates(),
        organizationAPI.getMembers(organizationId),
      ]);
      return {
        templates: (templatesRes.data as { templates?: PraiseTemplate[] })?.templates || [],
        members: (membersRes.data as { members?: OrganizationMember[] })?.members || [],
      };
    },
    enabled: isOpen && !!organizationId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    // Pre-migration loadData() had no retry wrapper.
    retry: false,
  });
  const templates = contextQuery.data?.templates ?? [];
  const members = contextQuery.data?.members ?? [];
  // Pre-migration set loading=true at the START of every loadData() call
  // (every open, not just the first) -- isFetching (not isLoading)
  // reproduces that for the staleTime:0-forced refetch-on-every-open above.
  const loading = contextQuery.isFetching;

  useEffect(() => {
    if (isOpen && preselectedRecipient) {
      setSelectedRecipient(preselectedRecipient.id);
    }
  }, [isOpen, preselectedRecipient]);

  useEffect(() => {
    // Mirrors pre-migration loadData()'s `setError('')` at the top of every
    // fetch, then `setError('Failed to load data')` only in the catch
    // branch -- a fetch starting clears any prior load error; a fetch
    // failing sets it.
    if (contextQuery.isFetching) {
      setError('');
    } else if (contextQuery.isError) {
      console.error('Failed to load data:', contextQuery.error);
      setError('Failed to load data');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextQuery.isFetching, contextQuery.isError]);

  // Derived at render time (not baked into state by the fetch), so it
  // recomputes automatically once useActiveRole's own independent query
  // resolves -- pre-migration computed this ONCE inside loadData(), which
  // didn't depend on myDynamicRole/myActiveRole, so if useActiveRole's fetch
  // resolved AFTER loadData() already ran, a switch member active as
  // Dominant could see the Points panel start hidden and never
  // self-correct. Incidental fix from the migration, noted here rather than
  // carried forward silently.
  //
  // 2026-07-05: was `role === 'admin' || role === 'super_admin'` — the
  // second branch checked org role against a value it can never hold
  // ('super_admin' is exclusively the platform user.role, checked
  // correctly via isDomRole's second argument here); the real,
  // previously-missed gap was the 'owner' org role.
  //
  // Spec 274 item 2: a switch's Dom-authority follows active_role, not
  // the raw org role — checked first so it overrides isDomRole for
  // switches (whose org role does not change on toggle). Non-switches
  // (myDynamicRole is null or not 'switch') keep the isDomRole check
  // unchanged, deliberately NOT routed through the dashboard's
  // resolveEffectiveRole fallback — that fallback treats any non-admin
  // org role as submissive, which would wrongly strip 'owner' members
  // of admin capability here.
  const currentUserMember = members.find((m: OrganizationMember) => m.userId === user?.id);
  const isAdmin =
    myDynamicRole === 'switch'
      ? myActiveRole === 'dominant'
      : isDomRole(currentUserMember?.role, user?.role);

  const handleTemplateSelect = (template: PraiseTemplate) => {
    setSelectedTemplate(template);
    setCustomMessage(template.message);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      setError('Please select an image or video file');
      return;
    }

    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size must be less than 50MB');
      return;
    }

    setMediaFile(file);
    setError('');

    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getMediaType = (file: File): 'image' | 'video' => {
    return file.type.startsWith('video/') ? 'video' : 'image';
  };

  const handleSend = async () => {
    if (!selectedRecipient) {
      setError('Please select a recipient');
      return;
    }

    const message = view === 'templates' && selectedTemplate ? selectedTemplate.message : customMessage;
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setSending(true);
    setError('');

    try {
      let mediaUrl: string | undefined;
      let mediaType: 'image' | 'video' | undefined;

      if (mediaFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', mediaFile);

        const uploadRes = await mediaAPI.upload(formData);
        mediaUrl = uploadRes.data.url;
        mediaType = getMediaType(mediaFile);
        setUploading(false);
      }

      await praiseAPI.send({
        recipientId: selectedRecipient,
        message: message.trim(),
        templateId: view === 'templates' && selectedTemplate ? selectedTemplate.id : undefined,
        points: isAdmin && customPoints !== '' ? customPoints : undefined,
        mediaUrl,
        mediaType,
      });

      queryClient.invalidateQueries({ queryKey: ['praise'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });

      triggerHaptic('success');
      setSuccess(true);
      if (onPraiseSent) {
        onPraiseSent();
      }

      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err: unknown) {
      setUploading(false);
      setError(isAxiosError(err) ? err.response?.data?.error || 'Failed to send praise' : 'Failed to send praise');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setSelectedRecipient(preselectedRecipient?.id || '');
    setCustomMessage('');
    setCustomPoints('');
    setError('');
    setSuccess(false);
    setView('templates');
    setMediaFile(null);
    setMediaPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const selectedMember = members.find((m: OrganizationMember) => m.userId === selectedRecipient);

  const footerActions = !success ? (
    <div className="flex gap-3 w-full">
      <Button variant="secondary" onClick={handleClose} disabled={sending || uploading} fullWidth>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSend}
        disabled={sending || uploading || !selectedRecipient || (view === 'templates' ? !selectedTemplate : !customMessage.trim())}
        fullWidth
      >
        {uploading ? 'Uploading...' : sending ? 'Sending...' : 'Send Praise'}
      </Button>
    </div>
  ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      // Success screen is intentionally chrome-free (no title bar) — pre-wave behavior
      title={success ? undefined : 'Send Quick Praise'}
      variant="sheet"
      footer={footerActions}
    >
      {success ? (
        <div className="py-12 text-center">
          <div className="text-[64px] mb-4">{'\uD83D\uDC9D'}</div>
          <h3 className="m-0 mb-2 text-2xl text-success">Praise Sent!</h3>
          <p className="m-0 text-text-secondary">Your praise has been delivered.</p>
        </div>
      ) : (
        <>
          {error && <div className="bg-danger/10 text-danger px-4 py-3 rounded mb-4 text-sm">{error}</div>}

          {loading ? (
            <div className="py-12 text-center text-text-tertiary"><SkeletonText lines={3} /></div>
          ) : (
            <>
              {/* Recipient Selection */}
              <div className="mb-5">
                <Select
                  value={selectedRecipient}
                  onChange={(e) => setSelectedRecipient(e.target.value)}
                  size="md"
                  label="To:"
                >
                  <option value="">Select recipient...</option>
                  {members.map((member: OrganizationMember) => (
                    <option key={member.userId} value={member.userId}>
                      {member.displayName || member.userName}
                    </option>
                  ))}
                </Select>
              </div>

              {/* View Toggle */}
              <TabBar
                tabs={[
                  { key: 'templates', label: 'Quick Templates' },
                  { key: 'custom', label: 'Custom Message' },
                ]}
                activeTab={view}
                onChange={(k) => setView(k as 'templates' | 'custom')}
                variant="pill"
                size="sm"
                className="mb-5"
              />

              {view === 'templates' ? (
                <div className="grid grid-cols-2 gap-3 mb-5">
                  {templates.map((template: PraiseTemplate) => (
                    <Button
                      key={template.id}
                      variant="ghost"
                      onClick={() => handleTemplateSelect(template)}
                      className={`flex flex-col items-center gap-2 py-4 px-3 bg-bg-tertiary border-2 border-transparent rounded transition-all hover:bg-bg-secondary hover:border-border-medium hover:-translate-y-0.5 h-auto min-h-0 ${selectedTemplate?.id === template.id ? 'bg-[rgba(184,115,51,0.1)] border-accent dark:bg-[rgba(184,115,51,0.15)]' : ''}`}
                    >
                      <span className="text-[28px]">{template.emoji}</span>
                      <span className="text-sm font-medium text-text-primary text-center">{template.title}</span>
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="mb-5">
                  <textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Write your praise message..."
                    className="w-full px-3 py-2.5 border border-border-medium rounded text-sm font-[inherit] resize-y min-h-[100px] bg-bg-secondary text-text-primary focus:outline-none focus:border-accent focus:shadow-[0_0_0_2px_rgba(184,115,51,0.2)]"
                    rows={4}
                    maxLength={500}
                  />
                  <div className="text-right text-xs text-text-tertiary mt-1">
                    {customMessage.length}/500
                  </div>
                </div>
              )}

              {/* Media Upload */}
              <div className="mb-5">
                <label className="block font-medium mb-2 text-text-primary">Add Photo or Video (optional)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="praise-media-input"
                />
                {!mediaPreview ? (
                  <label htmlFor="praise-media-input" className="flex flex-col items-center gap-2 border-2 border-dashed border-border-medium rounded p-6 text-center cursor-pointer transition-all bg-bg-tertiary hover:border-accent hover:bg-[rgba(184,115,51,0.05)]">
                    <span className="text-[32px]">{'\uD83D\uDCF7'}</span>
                    <span className="text-text-secondary text-sm">Click to add a photo or video</span>
                  </label>
                ) : (
                  <div className="relative mt-3 inline-block">
                    {mediaFile?.type.startsWith('video/') ? (
                      <video src={mediaPreview} className="max-w-full max-h-[200px] rounded block" controls />
                    ) : (
                      <img src={mediaPreview} alt="Preview" className="max-w-full max-h-[200px] rounded block" />
                    )}
                    <Button
                      type="button"
                      variant="danger"
                      onClick={handleRemoveMedia}
                      className="absolute -top-2 -right-2 w-6 h-6 min-w-0 min-h-0 p-0 rounded-full text-sm leading-none"
                    >
                      {'\u2715'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Points (admin only) */}
              {isAdmin && (
                <div className="mb-5 p-4 bg-bg-tertiary rounded border border-border-light">
                  <label className="block font-medium mb-2 text-text-primary">Points to Award</label>
                  <input
                    type="number"
                    min="0"
                    value={customPoints}
                    onChange={(e) => setCustomPoints(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                    placeholder="Use org default"
                    className="w-full px-3 py-2.5 border border-border-medium rounded text-sm bg-bg-secondary text-text-primary focus:outline-none focus:border-accent focus:shadow-[0_0_0_2px_rgba(184,115,51,0.2)]"
                  />
                  <small className="block mt-1.5 text-xs text-text-tertiary">
                    Leave empty to use the hierarchy's default praise points
                  </small>
                </div>
              )}

              {/* Preview */}
              {(selectedTemplate || customMessage) && selectedMember && (
                <div className="bg-bg-tertiary rounded p-4 mb-5 border border-border-light">
                  <div className="text-xs font-semibold uppercase tracking-wider text-text-tertiary mb-2">Preview:</div>
                  <div className="text-text-primary">
                    <strong className="block mb-1 text-accent">To {selectedMember.displayName || selectedMember.userName}:</strong>
                    <p className="m-0 leading-relaxed">{view === 'templates' && selectedTemplate ? selectedTemplate.message : customMessage}</p>
                    {mediaPreview && (
                      <span className="block mt-2 text-sm text-text-tertiary">
                        {mediaFile?.type.startsWith('video/') ? '\uD83D\uDCF9 Video attached' : '\uD83D\uDCF7 Photo attached'}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default QuickPraiseModal;
